import { RollbackOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Popconfirm,
  Table,
  TableColumnType,
  Typography,
} from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IQCResultByRequest } from "../../models/Qc";
import {
  useCreateReturnPartNumber,
  useGetQCResultByRequest,
} from "../../query/QcQuery";
import { notify } from "../../utils/toast";
import { QCStatus } from "../../enum/common";

const { Text } = Typography;

const QualityControlResultByAwb = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNumber = Number(id);
  const { data, isLoading } = useGetQCResultByRequest(idNumber);
  const [selectedReturnIds, setSelectedReturnIds] = useState<number[]>([]);
  const returnMutate = useCreateReturnPartNumber(idNumber);

  const handleSelectReturn = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedReturnIds((prev) => [...prev, id]);
    } else {
      setSelectedReturnIds((prev) => prev.filter((itemId) => itemId !== id));
    }
  };

  const handleSelectAllReturn = (checked: boolean) => {
    if (checked) {
      const allEligibleIds =
        data
          ?.filter((item) => item.isReturn === false)
          .map((item) => item.id) || [];
      setSelectedReturnIds(allEligibleIds);
    } else {
      setSelectedReturnIds([]);
    }
  };

  const handleReturn = async () => {
    try {
      await returnMutate.mutateAsync(selectedReturnIds);
      notify.success(`Đã hoàn trả ${selectedReturnIds.length} item`);
      setSelectedReturnIds([]);
    } catch (error: any) {
      notify.error(error?.response?.data?.message || "Không thể hoàn trả hàng");
    }
  };

  const columns: TableColumnType<IQCResultByRequest>[] = [
    {
      title: () => {
        const eligibleItems =
          data?.filter((item) => item.isReturn === false) || [];
        const allSelected =
          eligibleItems.length > 0 &&
          selectedReturnIds.length === eligibleItems.length;

        return (
          <Checkbox
            checked={allSelected}
            indeterminate={selectedReturnIds.length > 0 && !allSelected}
            onChange={(e) => handleSelectAllReturn(e.target.checked)}
            disabled={eligibleItems.length === 0}
          >
            Hoàn trả
          </Checkbox>
        );
      },
      key: "returnCheckbox",
      align: "center",
      width: 100,
      render: (_: any, record: IQCResultByRequest) => {
        const isAlreadyReturned = record.isReturn;

        return (
          <Checkbox
            checked={selectedReturnIds.includes(record.id)}
            onChange={(e) => handleSelectReturn(record.id, e.target.checked)}
            disabled={isAlreadyReturned}
          />
        );
      },
    },
    {
      title: "STT",
      key: "index",
      align: "center",
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Ngày QC",
      dataIndex: "qcDate",
      key: "qcDate",
      align: "center",
      width: 150,
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString("vi-VN") : "-",
    },
    {
      title: "Part Number",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
      width: 180,
      render: (partNumber: string) => (
        <Text strong style={{ color: "#1890ff" }}>
          {partNumber}
        </Text>
      ),
    },
    {
      title: "Loại NCC",
      dataIndex: ["partNumber", "supplierType"],
      key: "supplierType",
      align: "center",
      width: 120,
    },
    {
      title: "Nhà cung cấp",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 180,
      align: "center",
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      align: "center",
    },
    {
      title: "Số lượng kiểm tra",
      dataIndex: "samplingSize",
      key: "samplingSize",
      align: "center",
      width: 120,
      render: (quantity: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          {quantity}
        </Text>
      ),
    },
    {
      title: "Số lượng lỗi",
      dataIndex: "qtyNG",
      key: "qtyNG",
      align: "center",
      width: 120,
      render: (quantity: number) => (
        <Text strong style={{ color: quantity > 0 ? "#ff4d4f" : "#52c41a" }}>
          {quantity}
        </Text>
      ),
    },

    {
      title: "Kết luận",
      dataIndex: "result",
      key: "result",
      align: "center",
      width: 120,
      render: (result: string) => {
        let color = "#52c41a";
        if (result === QCStatus.REJECT) {
          color = "#ff4d4f";
        } else if (result === QCStatus.PARTIAL_REJECT) {
          color = "#fa8c16";
        }
        return (
          <Text strong style={{ color }}>
            {result}
          </Text>
        );
      },
    },
    {
      title: "Người mua",
      dataIndex: ["partNumber", "purchaser"],
      key: "purchaser",
      align: "center",
      width: 120,
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      key: "note",
      width: 200,
      align: "center",
      render: (note: string) => note || "-",
    },
    {
      title: "Trả hàng",
      dataIndex: "isReturn",
      key: "isReturn",
      align: "center",
      width: 100,
      render: (isReturn: boolean) =>
        isReturn ? (
          <Text type="success">Đã trả</Text>
        ) : (
          <Text type="danger">Chưa trả</Text>
        ),
    },
    {
      title: "Thao tác",
      dataIndex: "action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => navigate(`/quality-control/qc-issues/${record.id}`)}
        >
          Xem chi tiết
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4">
      <Popconfirm
        title="Xác nhận hoàn trả"
        description={`Bạn có chắc chắn muốn hoàn trả ${selectedReturnIds.length} item đã chọn?`}
        onConfirm={handleReturn}
        okText="Hoàn trả"
        cancelText="Hủy"
      >
        <Button
          type="primary"
          danger
          icon={<RollbackOutlined />}
          disabled={selectedReturnIds.length === 0}
          className="mb-4"
        >
          Hoàn trả ({selectedReturnIds.length})
        </Button>
      </Popconfirm>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data || []}
        loading={isLoading}
        pagination={false}
        tableLayout="auto"
        scroll={{ x: "max-content", y: 600 }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
        }}
        locale={{ emptyText: "Không có dữ liệu" }}
      />
    </div>
  );
};

export default QualityControlResultByAwb;
